import React, { useCallback, useMemo, useState } from 'react';

import {
  DisconnectOutlined,
  ExclamationOutlined,
  ExportOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { Button, Flex, Modal, Table, Tooltip, Typography } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { Link } from 'react-router-dom';

import { AssociateOrganizationModal } from './AssociateOrganizationModal';
import { ChildStatusCell } from './ChildStatusCell';
import { OrganizationEmptyState } from './OrganizationEmptyState';
import PillButton from '@/components/PillButton';
import { useDeleteDissociatePartner } from '@/features/lp-village/partners/api/deleteDissociatePartner';
import { useGetPartnerHierarchy } from '@/features/lp-village/partners/api/getPartnerHierarchy';
import { IPartner } from '@/features/lp-village/partners/models/partner';
import { IPartnerV3 } from '@/features/lp-village/partners/models/partnerV3';
const { Text } = Typography;

const PAGE_SIZE = 10;

interface IOrganizationTabProps {
  partner: IPartnerV3;
}

interface IChildRow {
  key: string;
  name: string;
  id: string;
  clientId: string;
  status: string;
  archived: boolean;
}

const BASE_COLUMNS: ColumnsType<IChildRow> = [
  {
    title: 'Client ID',
    dataIndex: 'clientId',
    key: 'clientId',
    render: (clientId: string) => {
      if (!clientId) return null;

      return <Text copyable={{ text: clientId }}>{clientId}</Text>;
    },
  },
  {
    title: 'Name',
    dataIndex: 'name',
    key: 'name',
    render: (_: string, record: IChildRow) => (
      <Link to={`/lp-village/partners/${record.id}`}>
        {record.name}{' '}
        <sup>
          <ExportOutlined />
        </sup>
      </Link>
    ),
  },
  {
    title: 'Status',
    dataIndex: 'status',
    key: 'status',
    render: (_: string, record: IChildRow) => (
      <ChildStatusCell
        partnerId={record.id}
        status={record.status}
        archived={record.archived}
      />
    ),
  },
];

const mapChildrenToRows = (childPartners: IPartner[]): IChildRow[] =>
  childPartners.map((child) => ({
    key: child.id,
    name: child.name,
    id: child.id,
    clientId: String(child.clientId || ''),
    status: child.businessInformation?.status || 'N/A',
    archived: child.archived,
  }));

export const OrganizationTab = ({ partner }: IOrganizationTabProps) => {
  const { data: hierarchy, isLoading } = useGetPartnerHierarchy(partner.id);
  const { mutateAsync: dissociate } = useDeleteDissociatePartner();

  const [modalOpen, setModalOpen] = useState(false);
  const [dissociatingPartnerId, setDissociatingPartnerId] = useState<
    string | null
  >(null);

  const childRows = useMemo(
    () => (hierarchy?.children ? mapChildrenToRows(hierarchy.children) : []),
    [hierarchy]
  );

  const handleDissociate = useCallback(
    (record: IChildRow) => {
      Modal.confirm({
        title: 'Unlink Organization',
        icon: <ExclamationOutlined />,
        content: `Are you sure you want to unlink "${record.name}" from this company? The partner will become a standalone partner.`,
        okText: 'Unlink',
        okButtonProps: { danger: true },
        onOk: async () => {
          setDissociatingPartnerId(record.id);
          try {
            await dissociate(record.id);
          } finally {
            setDissociatingPartnerId(null);
          }
        },
      });
    },
    [dissociate]
  );

  const columns: ColumnsType<IChildRow> = useMemo(
    () => [
      ...BASE_COLUMNS,
      {
        title: 'Actions',
        key: 'actions',
        width: 100,
        render: (_: unknown, record: IChildRow) => (
          <Tooltip title="Unlink from company">
            <Button
              data-testid={`organization-dissociate-${record.id}-button`}
              type="text"
              danger
              icon={<DisconnectOutlined />}
              loading={dissociatingPartnerId === record.id}
              onClick={() => handleDissociate(record)}
            />
          </Tooltip>
        ),
      },
    ],
    [handleDissociate, dissociatingPartnerId]
  );

  if (!partner.master_account_enabled) {
    return (
      <Flex vertical data-testid="organization-tab">
        <OrganizationEmptyState variant="not-enabled" />
      </Flex>
    );
  }

  return (
    <Flex vertical gap="middle" data-testid="organization-tab">
      <Flex justify="end">
        <PillButton
          data-testid="organization-associate-button"
          type="primary"
          onClick={() => setModalOpen(true)}
          icon={<PlusOutlined />}
        >
          Link organizations
        </PillButton>
      </Flex>

      {!isLoading && childRows.length === 0 && (
        <OrganizationEmptyState variant="no-children" />
      )}

      {childRows.length > 0 && (
        <Table<IChildRow>
          data-testid="organization-children-table"
          columns={columns}
          dataSource={childRows}
          pagination={
            childRows.length > PAGE_SIZE ? { pageSize: PAGE_SIZE } : false
          }
          loading={isLoading}
        />
      )}

      <AssociateOrganizationModal
        parentPartnerId={partner.id}
        open={modalOpen}
        onClose={() => setModalOpen(false)}
      />
    </Flex>
  );
};
